import type { CreateAdminRoleDto, UpdateAdminRoleDto } from '@idc-forms/api-client'
import type {
  V2CreateAdminRoleInput,
  V2UpdateAdminRoleInput
} from '@idc-forms/schemas/api-v2/admin-roles'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { rolesAdminV2Service } from '../services/roles-admin-v2.service'

/**
 * Mutaciones de roles contra la API v2. Identifican por id público (`rol_`) y,
 * al completar, invalidan todo el árbol `['roles']` (listado + summary + lookup
 * + detalle). El editor de página completa navega de vuelta al listado en el
 * `onSuccess` del consumidor.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

export const useCreateRoleV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    // Se acepta el tipo de DOMINIO (schema Zod) y se castea en el borde del SDK:
    // el generado tipa los permisos como unión de literales y el schema como enum
    // nominal — mismo dato en runtime, incompatibles para TS.
    mutationFn: async (body: V2CreateAdminRoleInput) =>
      await rolesAdminV2Service.create(accessToken, body as CreateAdminRoleDto),
    onSuccess: () => {
      toast.success('Rol creado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.roles.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useUpdateRoleV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: V2UpdateAdminRoleInput }) =>
      await rolesAdminV2Service.update(accessToken, id, body as UpdateAdminRoleDto),
    onSuccess: () => {
      toast.success('Rol actualizado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.roles.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useDeleteRoleV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async (id: string) => await rolesAdminV2Service.remove(accessToken, id),
    onSuccess: () => {
      toast.success('Rol eliminado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.roles.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
